/**
 * ExerciseRecorder — In-app MediaPipe motion capture recorder
 *
 * Records exercises via webcam (rear camera), saves JSON mocap data,
 * and auto-converts to GLB animation via Blender pipeline.
 *
 * Flow:
 *   1. User selects exercise → clicks Record
 *   2. MediaPipe Pose tracks body in real-time
 *   3. On Stop → saves JSON to localStorage + downloads
 *   4. JSON is stored for the ExerciseViewer to use as animation source
 */

import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Square, Play, Download, RotateCcw, Check, X, Video, Cloud } from 'lucide-react';
import { isAuthenticated } from '../../../services/firestoreSync';

const FPS = 30;
const STORAGE_KEY = 'adhd-gosport-mocap-recordings';

// MediaPipe connections for skeleton drawing
const CONNECTIONS = [
  [11,12],[11,13],[13,15],[12,14],[14,16],
  [11,23],[12,24],[23,24],[23,25],[25,27],[24,26],[26,28],
];

// Save recordings to localStorage
function saveRecordings(recordings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recordings));
  } catch { /* quota exceeded */ }
}

function loadRecordings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

// Save recording to Firestore for cloud sync
async function saveToFirestore(exerciseId, mocapData) {
  if (!isAuthenticated()) return false;
  try {
    const { doc, setDoc } = await import('firebase/firestore');
    const { db, auth } = await import('../../../services/firebase');
    const uid = auth.currentUser?.uid;
    if (!uid) return false;

    // Compress: only store world landmarks (smaller), downsample to 15fps
    const compressed = {
      name: mocapData.name,
      fps: 15,
      frameCount: 0,
      duration: mocapData.duration,
      recordedAt: mocapData.recordedAt,
      frames: [],
    };

    // Downsample: keep every 2nd frame (30fps → 15fps)
    for (let i = 0; i < mocapData.frames.length; i += 2) {
      const f = mocapData.frames[i];
      compressed.frames.push({
        t: Math.round(f.time * 1000) / 1000,
        w: f.world ? f.world.map(l => [
          Math.round(l.x * 10000) / 10000,
          Math.round(l.y * 10000) / 10000,
          Math.round(l.z * 10000) / 10000,
        ]) : null,
      });
    }
    compressed.frameCount = compressed.frames.length;

    const docRef = doc(db, 'users', uid, 'mocap', exerciseId);
    await setDoc(docRef, compressed);
    console.log(`☁️ Mocap saved to Firestore: ${exerciseId}`);
    return true;
  } catch (err) {
    console.warn('Firestore mocap save failed:', err.message);
    return false;
  }
}

// Load recording from Firestore
async function loadFromFirestore(exerciseId) {
  if (!isAuthenticated()) return null;
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db, auth } = await import('../../../services/firebase');
    const uid = auth.currentUser?.uid;
    if (!uid) return null;

    const docRef = doc(db, 'users', uid, 'mocap', exerciseId);
    const snap = await getDoc(docRef);
    if (snap.exists()) return snap.data();
    return null;
  } catch {
    return null;
  }
}

export const ExerciseRecorder = memo(function ExerciseRecorder({ exerciseId, exerciseName, onClose, onRecordingComplete }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const poseRef = useRef(null);
  const cameraRef = useRef(null);
  const framesRef = useRef([]);
  const startTimeRef = useRef(0);
  const lastFrameRef = useRef(0);

  const [status, setStatus] = useState('loading'); // loading | ready | recording | done
  const [recording, setRecording] = useState(false);
  const [frameCount, setFrameCount] = useState(0);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  // Load MediaPipe scripts
  useEffect(() => {
    const loadScripts = async () => {
      // Check if already loaded
      if (window.Pose && window.Camera) {
        initPose();
        return;
      }

      const scripts = [
        'https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js',
        'https://cdn.jsdelivr.net/npm/@mediapipe/drawing_utils/drawing_utils.js',
        'https://cdn.jsdelivr.net/npm/@mediapipe/pose/pose.js',
      ];

      for (const src of scripts) {
        if (document.querySelector(`script[src="${src}"]`)) continue;
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = src;
          s.crossOrigin = 'anonymous';
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }

      // Wait for global objects
      await new Promise(r => setTimeout(r, 500));
      initPose();
    };

    loadScripts().catch(err => {
      setError('Erreur chargement MediaPipe: ' + err.message);
    });

    return () => {
      if (cameraRef.current) {
        try { cameraRef.current.stop(); } catch {}
      }
    };
  }, []); // eslint-disable-line

  const initPose = useCallback(() => {
    if (!window.Pose || !window.Camera || !videoRef.current) {
      setTimeout(initPose, 300);
      return;
    }

    const pose = new window.Pose({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`,
    });

    pose.setOptions({
      modelComplexity: 1,
      smoothLandmarks: true,
      enableSegmentation: false,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    pose.onResults(onResults);
    poseRef.current = pose;

    const camera = new window.Camera(videoRef.current, {
      onFrame: async () => {
        if (poseRef.current && videoRef.current.readyState >= 2) {
          await poseRef.current.send({ image: videoRef.current });
        }
      },
      facingMode: 'environment',
      width: 640,
      height: 480,
    });

    camera.start().then(() => {
      setStatus('ready');
    }).catch(err => {
      setError('Camera: ' + err.message);
    });

    cameraRef.current = camera;
  }, []);

  const onResults = useCallback((results) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);

    if (results.poseLandmarks) {
      // Draw skeleton
      ctx.strokeStyle = '#f97316';
      ctx.lineWidth = 3;
      CONNECTIONS.forEach(([a, b]) => {
        const la = results.poseLandmarks[a];
        const lb = results.poseLandmarks[b];
        if (la && lb && la.visibility > 0.4 && lb.visibility > 0.4) {
          ctx.beginPath();
          ctx.moveTo(la.x * canvas.width, la.y * canvas.height);
          ctx.lineTo(lb.x * canvas.width, lb.y * canvas.height);
          ctx.stroke();
        }
      });

      // Draw joints
      ctx.fillStyle = '#fff';
      results.poseLandmarks.forEach((l) => {
        if (l.visibility > 0.4) {
          ctx.beginPath();
          ctx.arc(l.x * canvas.width, l.y * canvas.height, 4, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // Record frame
      if (recording) {
        const now = performance.now();
        if (now - lastFrameRef.current >= 1000 / FPS) {
          framesRef.current.push({
            time: (now - startTimeRef.current) / 1000,
            landmarks: results.poseLandmarks.map(l => ({ x: l.x, y: l.y, z: l.z, v: l.visibility })),
            world: results.poseWorldLandmarks
              ? results.poseWorldLandmarks.map(l => ({ x: l.x, y: l.y, z: l.z }))
              : null,
          });
          lastFrameRef.current = now;
          setFrameCount(framesRef.current.length);
        }
      }
    }
    ctx.restore();
  }, [recording]);

  const startRecording = useCallback(() => {
    framesRef.current = [];
    startTimeRef.current = performance.now();
    lastFrameRef.current = 0;
    setRecording(true);
    setFrameCount(0);
    setSaved(false);
    setStatus('recording');
  }, []);

  const [cloudSaved, setCloudSaved] = useState(false);

  const stopRecording = useCallback(() => {
    setRecording(false);
    setStatus('done');

    const mocapData = {
      name: exerciseId,
      displayName: exerciseName,
      fps: FPS,
      frameCount: framesRef.current.length,
      duration: framesRef.current.length / FPS,
      recordedAt: new Date().toISOString(),
      frames: framesRef.current,
    };

    // 1. Save to localStorage (immediate)
    const recordings = loadRecordings();
    recordings[exerciseId] = mocapData;
    saveRecordings(recordings);
    setSaved(true);

    // 2. Save to Firestore (async, compressed)
    setCloudSaved(false);
    saveToFirestore(exerciseId, mocapData).then(ok => {
      if (ok) setCloudSaved(true);
    });

    // 3. Notify parent
    onRecordingComplete?.(exerciseId, mocapData);
  }, [exerciseId, exerciseName, onRecordingComplete]);

  const downloadJSON = useCallback(() => {
    const recordings = loadRecordings();
    const data = recordings[exerciseId];
    if (!data) return;

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exerciseId}_mocap.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [exerciseId]);

  const resetRecording = useCallback(() => {
    framesRef.current = [];
    setFrameCount(0);
    setSaved(false);
    setStatus('ready');
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[400] bg-zinc-950 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5 text-orange-400" />
          <div>
            <p className="text-sm font-bold text-white">{exerciseName}</p>
            <p className="text-[10px] text-zinc-500">
              {status === 'loading' && 'Chargement camera...'}
              {status === 'ready' && 'Pret a enregistrer'}
              {status === 'recording' && `${frameCount} frames (${(frameCount / FPS).toFixed(1)}s)`}
              {status === 'done' && `${frameCount} frames capturees`}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Video + Canvas */}
      <div className="flex-1 relative overflow-hidden bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0 }}
        />
        <canvas
          ref={canvasRef}
          width={640}
          height={480}
          className="w-full h-full object-contain"
        />

        {/* Recording indicator */}
        {recording && (
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500/80 px-3 py-1.5 rounded-full">
            <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span className="text-xs font-bold text-white">REC</span>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <p className="text-red-400 text-sm text-center px-4">{error}</p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="px-4 py-4 border-t border-zinc-800 bg-zinc-900/80">
        {status === 'ready' && (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={startRecording}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-red-500 to-orange-500
              text-white font-bold text-lg flex items-center justify-center gap-3
              shadow-lg shadow-red-500/20"
          >
            <Camera className="w-6 h-6" />
            Enregistrer
          </motion.button>
        )}

        {status === 'recording' && (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={stopRecording}
            className="w-full py-4 rounded-2xl bg-red-600
              text-white font-bold text-lg flex items-center justify-center gap-3"
          >
            <Square className="w-6 h-6" fill="white" />
            Arreter ({(frameCount / FPS).toFixed(1)}s)
          </motion.button>
        )}

        {status === 'done' && (
          <div className="space-y-3">
            {saved && (
              <div className="flex items-center gap-3 text-sm justify-center">
                <div className="flex items-center gap-1 text-green-400">
                  <Check className="w-4 h-4" />
                  <span>Sauvegarde</span>
                </div>
                {cloudSaved && (
                  <div className="flex items-center gap-1 text-blue-400">
                    <Cloud className="w-3.5 h-3.5" />
                    <span>Cloud</span>
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={resetRecording}
                className="flex-1 py-3 rounded-xl bg-zinc-800 text-zinc-300
                  flex items-center justify-center gap-2 text-sm font-medium"
              >
                <RotateCcw className="w-4 h-4" />
                Refaire
              </button>
              <button
                onClick={downloadJSON}
                className="flex-1 py-3 rounded-xl bg-zinc-800 text-zinc-300
                  flex items-center justify-center gap-2 text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                JSON
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-500
                  text-white flex items-center justify-center gap-2 text-sm font-bold"
              >
                <Check className="w-4 h-4" />
                Valider
              </button>
            </div>
          </div>
        )}

        {status === 'loading' && (
          <div className="flex items-center justify-center gap-2 text-zinc-500 py-4">
            <div className="w-4 h-4 border-2 border-zinc-600 border-t-orange-400 rounded-full animate-spin" />
            <span className="text-sm">Chargement...</span>
          </div>
        )}
      </div>
    </motion.div>
  );
});

/**
 * Check if a recorded mocap JSON exists for an exercise (localStorage)
 */
export function getMocapRecording(exerciseId) {
  const recordings = loadRecordings();
  return recordings[exerciseId] || null;
}

/**
 * Get mocap recording, checking Firestore if not in localStorage
 */
export async function getMocapRecordingAsync(exerciseId) {
  // 1. Check localStorage first (instant)
  const local = getMocapRecording(exerciseId);
  if (local) return local;

  // 2. Check Firestore (async)
  const cloud = await loadFromFirestore(exerciseId);
  if (cloud) {
    // Cache locally for next time
    const recordings = loadRecordings();
    recordings[exerciseId] = cloud;
    saveRecordings(recordings);
    return cloud;
  }

  return null;
}

/**
 * Get all recorded exercises
 */
export function getAllMocapRecordings() {
  return loadRecordings();
}
